import {
	AspectRatio,
	Box,
	Button,
	Heading,
	HStack,
	Image,
	Skeleton,
	Text,
	VStack,
} from "@chakra-ui/react";
import apiFetch from "@wordpress/api-fetch";
import { parse } from "@wordpress/blocks";
import { useDispatch } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import { store as noticesStore } from "@wordpress/notices";
import React from "react";
import { useMutation, useQuery } from "react-query";
import axios from "redaxios";
import { saveImageLocally } from "../../helpers";
import { withChakraUIProvider, withQueryClientProvider } from "../../hoc";
import { LibraryDataResponse } from "../../types";
import { ScrollArea } from "../scroll-area/ScrollArea";

const Card = ({
	imageUrl,
	label,
	count,
	action,
	type,
	id,
}: {
	imageUrl: string;
	label: string;
	count?: number;
	action?: () => void;
	type: "cat" | "section";
	id?: number;
}) => {
	const { createSuccessNotice, createErrorNotice } =
		useDispatch(noticesStore);
	const { mutate, isLoading } = useMutation(
		async (_id: number) => {
			try {
				const response = await axios.get(
					`https://blocks.themegrilldemos.com/wp-json/blockart-library/v1/mzb/content?id=${_id}`
				);
				const content = await saveImageLocally(response?.data ?? "");
				//@ts-ignore
				const blocks = parse(content, {
					__unstableSkipMigrationLogs: true,
				});
				wp.data.dispatch("core/block-editor").insertBlocks(blocks);
				return _id;
			} catch {
				throw Error("Failed to import");
			}
		},
		{
			onSuccess() {
				createSuccessNotice(__("Import success!", "magazine-blocks"), {
					type: "snackbar",
				});
			},
			onError() {
				createErrorNotice(__("Import failed!", "magazine-blocks"), {
					type: "snackbar",
				});
			},
		}
	);
	return (
		<Box
			w="full"
			border="1px"
			borderColor="gray.200"
			borderRadius="sm"
			cursor="pointer"
			_hover={{
				borderColor: "primary.500",
			}}
			onClick={type === "cat" ? action : undefined}
		>
			<AspectRatio ratio={4 / 3} m="4px 4px 0 4px">
				<Image src={imageUrl} />
			</AspectRatio>
			<HStack justify="space-between" py="3" px="4">
				<Heading
					m="0"
					fontSize="11px"
					fontWeight="semibold"
					as="h3"
					size="md"
				>
					{label}
				</Heading>
				{type === "cat" ? (
					<Text
						as="span"
						color="primary.500"
						fontSize="11px"
						fontWeight="semibold"
					>
						{count}
					</Text>
				) : (
					<Button
						onClick={id ? () => mutate(id) : undefined}
						fontSize="x-small"
						size="xs"
						isLoading={isLoading}
					>
						{__("Import", "magazine-blocks")}
					</Button>
				)}
			</HStack>
		</Box>
	);
};

export const LibrarySidebar = () => {
	const libraryQuery = useQuery(["library-data"], () =>
		apiFetch<LibraryDataResponse>({
			path: `/magazine-blocks/v1/library-data`,
			method: "GET",
		})
	);

	const [activeCat, setActiveCat] = React.useState<string>();
	const ref = React.useRef<HTMLDivElement>(null);

	const closeLibraryPanel = () => {
		document.querySelector(".mzb-library-sidebar")?.remove();
	};

	return (
		<ScrollArea h="full">
			<Box h="calc(100% - 35px)" w="auto" p="20px">
				<Heading
					as="h3"
					fontSize="13px"
					fontWeight="semibold"
					m="0"
					alignSelf="start"
					mb="6"
					cursor="pointer"
					onClick={() => setActiveCat(undefined)}
				>
					{activeCat
						? __("Go back", "magazine-blocks")
						: __("Library", "magazine-blocks")}
				</Heading>
				<VStack
					gap="4"
					w="calc(100% + 20px)"
					h="calc(100%)"
					overflowY="auto"
					sx={{
						"&::-webkit-scrollbar": {
							width: "4px",
							backgroundColor: `white`,
						},
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: "gray.300",
							borderRadius: "8px",
						},
					}}
					pr="20px"
					mr="-20px"
					ref={ref}
				>
					{libraryQuery.isLoading || libraryQuery.isRefetching
						? Array.from({ length: 5 }).map((_, i) => (
								<Box
									key={i}
									w="full"
									border="1px"
									borderColor="gray.200"
									borderRadius="sm"
									p="4px"
								>
									<Skeleton h="170px" w="full" />
									<HStack
										justify="space-between"
										py="3"
										px="4"
									>
										<Skeleton w="50px" h="14px" />
										<Skeleton w="14px" h="14px" />
									</HStack>
								</Box>
						  ))
						: !!libraryQuery.data?.categorized_sections
						? activeCat
							? libraryQuery.data.categorized_sections[
									activeCat
							  ].items.map((s) => (
									<Card
										key={s.ID}
										label={s.post_title}
										imageUrl={s.post_thumbnail}
										action={() => setActiveCat(undefined)}
										type="section"
										id={s.ID}
									/>
							  ))
							: Object.values(
									libraryQuery.data.categorized_sections
							  ).map((cat) => (
									<Card
										key={cat.slug}
										label={cat.name}
										imageUrl={cat.items[0].post_thumbnail}
										action={() => setActiveCat(cat.slug)}
										count={cat.count}
										type="cat"
									/>
							  ))
						: null}
				</VStack>
			</Box>
		</ScrollArea>
	);
};

export default withChakraUIProvider(withQueryClientProvider(LibrarySidebar));
